import { ResourceTypeLink } from "@evergis/api";
import { Sort } from "@evergis/ui";
import { produce } from "immer";
import { defaultFetchParams, ResourceCategory } from "modules/Catalog/hooks/useFetchParams";
import { createAction, createReducer } from "redux-act";

import { reducerRegistry } from "../reducerRegistry";
import { findResourceIndex, removeResource } from "./helpers";
import { ResourceViewType } from "../../modules/Catalog/components/ViewType/types";

import { ResourceInfoDc } from "../../modules/Catalog/types";

export const reducerName = "catalog";

export type SelectedResource = Partial<ResourceInfoDc & { isFavorite: boolean }>;

interface ResourceFetchParams {
  filter: string;
  sort: Sort;
  category: ResourceCategory;
  geometryFilter?: string;
  types?: string[];
  limit?: number;
  offset?: number;
}

export interface CatalogState {
  resourceEmpty: boolean;
  selected: SelectedResource[];
  fetchParams: {
    projects: ResourceFetchParams;
    layers: ResourceFetchParams;
    tables: ResourceFetchParams;
  };
  viewType?: ResourceViewType;
}

const initialState = {
  resourceEmpty: true,
  selected: [],
  fetchParams: {
    projects: defaultFetchParams,
    layers: defaultFetchParams,
    tables: defaultFetchParams,
  },
  viewType: ResourceViewType.Grid,
};

type FetchParameter<Value> = {
  type: ResourceTypeLink;
  value: Value;
};

export const reducer = createReducer({}, initialState);
export const setFilter = createAction<FetchParameter<string>>("catalog/set-filter");
export const setCategory = createAction<FetchParameter<ResourceCategory>>("catalog/set-category");
export const setSort = createAction<FetchParameter<Sort>>("catalog/set-sort");
export const setOffset = createAction<FetchParameter<number>>("catalog/set-offset");
export const setLimit = createAction<FetchParameter<number>>("catalog/set-limit");
export const setViewType = createAction<ResourceViewType>("catalog/set-view-type");
export const setGeometryFilter = createAction<FetchParameter<string>>("catalog/set-geometry-filter");
export const setServiceTypeFilter = createAction<FetchParameter<string[]>>("catalog/set-service-type-filter");
export const selectResource = createAction<{ resource: SelectedResource }>("catalog/select-resource");
export const unselectResource = createAction<{ resourceName: string }>("catalog/unselect-resource");
export const setSelected = createAction<{ resources: SelectedResource[] }>("catalog/set-selected");
export const resetSelected = createAction("catalog/reset-selected");
export const setResourceEmpty = createAction<boolean>("catalog/setResourceEmpty");
export const resetState = createAction("catalog/resetState");

reducer.on(
  setFilter,
  produce((state: CatalogState, { type, value }: FetchParameter<string>) => {
    state.fetchParams[type].filter = value;
  }),
);

reducer.on(
  setCategory,
  produce((state: CatalogState, { type, value }: FetchParameter<ResourceCategory>) => {
    state.fetchParams[type].category = value;
  }),
);

reducer.on(
  setSort,
  produce((state: CatalogState, { type, value }: FetchParameter<Sort>) => {
    state.fetchParams[type].sort = value;
  }),
);

reducer.on(
  setOffset,
  produce((state: CatalogState, { type, value }: FetchParameter<number>) => {
    state.fetchParams[type].offset = value;
  }),
);

reducer.on(
  setLimit,
  produce((state: CatalogState, { type, value }: FetchParameter<number>) => {
    state.fetchParams[type].limit = value;
  }),
);

reducer.on(
  setViewType,
  produce((state: CatalogState, viewType: ResourceViewType) => {
    state.viewType = viewType;
  }),
);

reducer.on(
  setGeometryFilter,
  produce((state: CatalogState, { type, value }: FetchParameter<string>) => {
    state.fetchParams[type].geometryFilter = value;
  }),
);

reducer.on(
  setServiceTypeFilter,
  produce((state: CatalogState, { type, value }: FetchParameter<string[]>) => {
    state.fetchParams[type].types = value;
  }),
);

reducer.on(
  selectResource,
  produce((state: CatalogState, { resource }: { resource: SelectedResource }) => {
    if (findResourceIndex(state.selected, resource.name) === -1) {
      state.selected = [...state.selected, resource];
    }
  }),
);

reducer.on(
  unselectResource,
  produce((state: CatalogState, { resourceName }: { resourceName: string }) => {
    state.selected = removeResource(state.selected, resourceName);
  }),
);

reducer.on(
  setResourceEmpty,
  produce((state: CatalogState, resourceEmpty: boolean) => {
    state.resourceEmpty = resourceEmpty;
  }),
);

reducer.on(
  setSelected,
  produce((state: CatalogState, { resources }: { resources: SelectedResource[] }) => {
    state.selected = resources;
  }),
);

reducer.on(
  resetSelected,
  produce((state: CatalogState) => {
    state.selected = [];
  }),
);

reducer.on(
  resetState,
  produce((state: CatalogState) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-param-reassign
    state = { ...initialState };
  }),
);

reducerRegistry.register(reducerName, reducer);
